import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback
} from "react";
import { ReportType, SettingsPeriod } from "@/entities/reporting/types";
import { ReservedUserIds, UserData } from "@/entities/users/types";
import { useUsers } from "@/entities/users/hooks";
import useBoundStore from "@/services/store/BoundStore";
import { useDirectReportsCheck } from "./useDirectReportsCheck";

const EMPTY_ARRAY: UserData[] = [];

interface BreadcrumbsContextType<TDirectReportsResponse> {
  selectedUserId: string;
  setSelectedUserId: (id: string) => void;
  dynamicTableData: UserData[];
  breadcrumbIds: string[];
  handleRowClick: (rowData: UserData) => void;
  directReportsData?: TDirectReportsResponse;
  isLoading: boolean;
  error: unknown;
}

interface BreadcrumbsConfig {
  reportType: ReportType;
  responseKey: string;
  defaultPeriod: SettingsPeriod;
  getUserIds: (data: any) => string[];
}

export function createDirectReportsBreadcrumbsContext<TDirectReportsResponse>(
  config: BreadcrumbsConfig
) {
  const { reportType, responseKey, defaultPeriod, getUserIds } = config;

  const BreadcrumbsContext = createContext<
    BreadcrumbsContextType<TDirectReportsResponse> | undefined
  >(undefined);

  const BreadcrumbsProvider: React.FC<{
    children: React.ReactNode;
    leadUser: UserData | undefined;
    period?: SettingsPeriod;
    enableDirectReportsData?: boolean;
    initialDirectReportsData?: TDirectReportsResponse;
  }> = ({
    children,
    leadUser,
    period,
    enableDirectReportsData = true,
    initialDirectReportsData
  }) => {
    const getUser = useBoundStore((state) => state.users.getEntity);
    const [selectedUserId, setSelectedUserId] = useState<string>(
      ReservedUserIds.UNKNOWN_USER_ID
    );

    useEffect(() => {
      setSelectedUserId(
        leadUser ? leadUser.id : ReservedUserIds.UNKNOWN_USER_ID
      );
    }, [leadUser]);

    const selectedUser = useUsers([selectedUserId]).users[0];

    // Use the hook for nested navigation (when drilling down into a direct report's reports)
    const {
      directReportsData: fetchedData,
      isLoading: isFetching,
      error
    } = useDirectReportsCheck<TDirectReportsResponse>(
      selectedUserId,
      period || defaultPeriod,
      enableDirectReportsData &&
        selectedUserId !== ReservedUserIds.UNKNOWN_USER_ID &&
        selectedUserId !== leadUser?.id, // Only fetch when navigating to a different user
      {
        reportType,
        responseKey
      }
    );

    const responseData = useMemo(() => {
      if (selectedUserId === leadUser?.id && initialDirectReportsData) {
        return initialDirectReportsData;
      }
      return fetchedData;
    }, [selectedUserId, leadUser, initialDirectReportsData, fetchedData]);

    const directReportIds = useMemo(() => {
      return getUserIds(responseData);
    }, [responseData]);

    const directReportsDataUsers = useUsers(directReportIds).users;

    const handleRowClick = useCallback((rowData: UserData) => {
      if (
        rowData &&
        rowData?.directReports &&
        rowData?.directReports?.length > 0
      )
        setSelectedUserId(rowData.id);
    }, []);

    const dynamicTableData: UserData[] = useMemo(() => {
      if (!directReportsDataUsers || directReportsDataUsers.length === 0)
        return EMPTY_ARRAY;
      return directReportsDataUsers;
    }, [directReportsDataUsers]);

    const buildBreadcrumbPath = useCallback(
      (
        user: UserData | undefined,
        leadUserParam: UserData | undefined,
        path: string[] = []
      ): string[] => {
        if (!user || !leadUserParam) {
          return path;
        }
        path.unshift(user.id);
        if (user.id === leadUserParam.id || !user.managerId) {
          return path;
        }
        const manager = getUser(user.managerId);
        return buildBreadcrumbPath(manager, leadUserParam, path);
      },
      [getUser]
    );

    const breadcrumbIds = useMemo(
      () => buildBreadcrumbPath(selectedUser, leadUser),
      [selectedUser, leadUser, buildBreadcrumbPath]
    );

    const value = useMemo(
      () => ({
        selectedUserId,
        setSelectedUserId,
        dynamicTableData,
        breadcrumbIds,
        handleRowClick,
        directReportsData: responseData,
        isLoading: selectedUserId === leadUser?.id ? false : isFetching,
        error
      }),
      [
        selectedUserId,
        dynamicTableData,
        breadcrumbIds,
        handleRowClick,
        responseData,
        isFetching,
        error,
        leadUser
      ]
    );

    return (
      <BreadcrumbsContext.Provider value={value}>
        {children}
      </BreadcrumbsContext.Provider>
    );
  };

  const useBreadcrumbs = () => {
    const context = useContext(BreadcrumbsContext);
    if (context === undefined) {
      throw new Error(
        "useBreadcrumbs must be used within a BreadcrumbsProvider"
      );
    }
    return context;
  };

  return { BreadcrumbsProvider, useBreadcrumbs };
}
